import { Trans } from '@lingui/macro';
import { NavigateBefore, NavigateNext, Edit } from '@mui/icons-material';
import { Button, ButtonGroup, Grid, Typography } from '@mui/material';
import { DateTime } from 'luxon';
import { Fragment, useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import { enterpriseService } from 'services';
import { formatNumber } from 'utils/common';
import { IS_EPAYZ } from 'env';
import { TooltipLight } from 'components';
import { EnterprisePaymentScheduleNote } from 'views/Enterprise/EnterpriseList/components';
import { getDay } from 'constants/common';

const EnterprisePaymentSchedule = () => {
  const [pivot, setPivot] = useState(0);
  const pivotWeek = DateTime.now().plus({ week: pivot });
  const workingDays = Array.from({ length: 5 }).map((_, index) => pivotWeek.startOf('week').plus({ day: index }));

  const weekSearch = useMemo(() => {
    return {
      dateFrom: pivotWeek.startOf('week').toISO(),
      dateTo: pivotWeek.endOf('week').toISO(),
    };
  }, [pivotWeek]);

  const { data: weekly } = useQuery(['enterpriseService.fetchPaymentSchedule', { ...weekSearch }], () =>
    enterpriseService.fetchPaymentSchedule({ ...weekSearch }),
  );

  const [openTooltip, setOpenTooltip] = useState(null);

  const handleOpen = (index: any) => {
    setOpenTooltip(openTooltip === index ? null : index);
  };

  return (
    <div>
      <Typography variant='h4' className='mb-2'>
        <Trans>Payment Schedule of Enterprise</Trans>
      </Typography>

      <div className='flex items-start'>
        <Grid container className='text-center'>
          <Grid item sm={2} className='relative bg-primary-light text-white border p-2'>
            <div>
              <Trans>WEEK</Trans> {pivotWeek.weekNumber}
            </div>
            <div>{pivotWeek.year}</div>
          </Grid>
          {workingDays.map((today, index) => (
            <Grid item key={index} sm={2} className='bg-primary-light text-white border p-2'>
              <div>{getDay(today.weekdayLong.toUpperCase())}</div>
              <div>{today.toFormat('dd/MM/yyyy')}</div>
            </Grid>
          ))}

          <Grid item sm={2} className='border p-2'>
            <ButtonGroup variant='contained' size='small'>
              <Button onClick={() => setPivot((v) => v - 1)}>
                <NavigateBefore />
              </Button>
              <Button disabled={pivot === 0} onClick={() => setPivot(0)}>
                <Trans>TODAY</Trans>
              </Button>
              <Button onClick={() => setPivot((v) => v + 1)}>
                <NavigateNext />
              </Button>
            </ButtonGroup>
          </Grid>
          {workingDays.map((today, index) => {
            const { data: items = [] } = weekly ?? {};
            const paymentSchedules = items.find((item) =>
              DateTime.fromISO(item.paymentDate).startOf('day').equals(today),
            );
            return (
              <Grid item key={index} sm={2} className='border p-2'>
                <div className='flex justify-center space-x-4'>
                  <Typography variant='h4' className='mb-2'>
                    {formatNumber(paymentSchedules?.totalCollectionAmount)}
                  </Typography>
                  <TooltipLight
                    className={`max-w-[800px] min-w-[300px] ${IS_EPAYZ ? 'epayz' : 'admin'}`}
                    disableFocusListener
                    title={<EnterprisePaymentScheduleNote date={today.toISO()} />}
                    open={openTooltip === index}
                  >
                    <Edit fontSize='small' className='hover:text-primary-main' onClick={() => handleOpen(index)} />
                  </TooltipLight>
                </div>
                {paymentSchedules?.enterprises.map((payment, index) => (
                  <Fragment key={index}>
                    <Grid
                      item
                      sm={12}
                      className={`${
                        payment.collectionAmount ? 'bg-gray-300' : 'bg-primary-light text-white'
                      } border-b p-1`}
                    >
                      <div className='text-sm'>{payment.enterpriseName}</div>
                      <div>{`(${payment.employeeNumber} - ${
                        payment.collectionAmount
                          ? formatNumber(payment.collectionAmount)
                          : formatNumber(payment.serviceCost)
                      })`}</div>
                    </Grid>
                  </Fragment>
                ))}
              </Grid>
            );
          })}
        </Grid>

        <Grid container className='text-center w-60 ml-2'>
          <Grid item sm={12} className='bg-primary-light text-white border p-2'>
            <Trans>TOTAL COLLECTION AMOUNT IN WEEK (VND)</Trans>
          </Grid>
          <Grid item sm={12} className='border p-2'>
            <Typography variant='h4' className='mb-2'>
              {formatNumber(weekly?.totalCollectionAmountInWeek) ?? 0}
            </Typography>
          </Grid>
          <Grid item sm={12} className='bg-primary-light text-white border p-2'>
            <Trans>TOTAL COLLECTION AMOUNT IN MONTH (VND)</Trans>
          </Grid>
          <Grid item sm={12} className='border p-2'>
            <Typography variant='h4' className='mb-2'>
              {formatNumber(weekly?.totalCollectionAmountInMonth) ?? 0}
            </Typography>
          </Grid>
        </Grid>
      </div>
    </div>
  );
};

export default EnterprisePaymentSchedule;
